<template>
  <div>
    <div
      class="text-center"
      v-if="!dataLoaded"
    >
      <v-progress-circular
        class="mt-6"
        color="primary"
        :size="50"
        indeterminate
      />
    </div>

    <div
      class="mt-4"
      v-show="dataLoaded"
    >
      <TitleCard
        :is-show-mode="isShowMode"
        :is-process-mode="isProcessMode"
        :is-edit-mode="isEditMode"
        :disabled="invalid"
        :saveLoading="saveBtnLoading"
        :selected-item="monography"
        :is-no-changes="isNoChanges"
        @save="saveMonography"
      />

      <v-row
        class="mb-0 mt-2"
        style="margin-right: -14px"
      >
        <v-col
          ref="workList"
          cols="12"
          class="pt-1 pb-2 pr-2 overflow-auto"
        >
          <WorkTitleCard :title="this.workTypeRusName(workTypeName.Monography)" />

          <AuthorsCard
            class="mt-4"
            title="Автор"
            :agreementFileList="agreementFileList"
            :selected-item.sync="monography.commonWork.authors"
            :is-show-mode="isShowMode"
            :is-foreign-publication="monography.isForeign"
            :upload-files="true"
            is-show-isforeignauthor
            @updateInvalid="updateAuthorsInvalid"
            @uploadAgreementFile="initUploadAgreementFile"
            @removeAgreementFile="initRemoveAgreementFile"
          >
            <CardRow>
              <v-checkbox
                class="rounded-lg mini-text field-edit d-inline-block"
                v-model="monography.isForeign"
                label="Монография на иностранном языке"
                hide-details
                dense
                :disabled="isShowMode"
              />
            </CardRow>
          </AuthorsCard>

          <MonographyCard
            class="mt-4"
            title="Основная информация"
            :linkFileList="linkFileList"
            :is-show-mode="isShowMode"
            :selected-item.sync="monography"
            @updateInvalid="updateMonographyInvalid"
            @uploadLinkFiles="initUploadLinkFiles"
            @removeLinkFile="initRemoveLinkFile"
          />

          <PublisherInformationCard
            class="mt-4"
            :selected-item.sync="monography"
            :is-show-mode="isShowMode"
            @updateInvalid="updatePublisherInformationCardInvalid"
          />

          <PagesInTheEditionCard
            class="mt-4"
            :selected-item.sync="monography"
            :is-show-mode="isShowMode"
            @updateInvalid="updatePagesInTheEditionCardInvalid"
          />

          <CommentCard
            v-if="Boolean(monography.commonWork.comment)"
            class="mt-4"
            :comment="monography.commonWork.comment"
          />

          <CreatorCard :creator-name="monography.commonWork.creator.fullName" />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { monographyApi } from '@/api'
import { Monography } from '../../models'
import MonographyCard from '../../components/Works/Monography/MonographyCard'
import TitleCard from '@/components/Works/elements/TitleCard'
import PagesInTheEditionCard from '../../components/Works/Monography/PagesInTheEditionCard'
import PublisherInformationCard from '../../components/Works/Monography/PublisherInformationCard'
import AuthorsCard from '../../components/Works/elements/AuthorsCard'
import CommentCard from '@/components/Works/elements/CommentCard.vue'
import { mapActions, mapGetters } from 'vuex'
import { workListHeight } from '@/mixins/workListHeight'
import { dictionaryNames, fileTypeId, workMode, workTypeName } from '@/scripts/consts'
import WorkTitleCard from '@/components/Works/elements/WorkTitleCard.vue'
import CreatorCard from '@/components/Works/elements/CreatorCard.vue'
import CardRow from '@/components/ui/row/CardRow.vue'
import { filesForWork } from '@/mixins/filesForWork'

export default {
  name: 'MonographyPage',

  metaInfo() {
    return {
      title: this && this.workTypeRusName(workTypeName.Monography)
    }
  },

  components: {
    CardRow,
    CreatorCard,
    WorkTitleCard,
    CommentCard,
    AuthorsCard,
    PublisherInformationCard,
    PagesInTheEditionCard,
    TitleCard,
    MonographyCard
  },

  mixins: [workListHeight, filesForWork],

  created() {
    this.isShowMode = this.$route.params.mode === workMode.Show
    this.isProcessMode = this.$route.params.mode === workMode.Process
    this.isEditMode = this.$route.params.mode === workMode.Edit
    this.monography = new Monography({
      commonWork: {
        isEffContract: true
      }
    })
    this.getData()
  },

  data: () => ({
    monography: new Monography(),
    dataLoaded: false,
    isShowMode: false,
    isEditMode: false,
    isProcessMode: false,
    monographyInvalid: false,
    authorsInvalid: false,
    pagesInTheEditionCardInvalid: false,
    publisherInformationCardInvalid: false,
    saveBtnLoading: false,
    isNoChanges: true
  }),

  computed: {
    workTypeName() {
      return workTypeName
    },
    ...mapGetters('workTypes', ['workTypeByName', 'workTypeRusName']),

    invalid() {
      return (
        this.monographyInvalid ||
        this.authorsInvalid ||
        this.pagesInTheEditionCardInvalid ||
        this.publisherInformationCardInvalid
      )
    }
  },

  methods: {
    ...mapActions('dictionaries', ['getDictionaries']),
    ...mapActions('workTypes', ['getWorkTypes']),

    async getData() {
      if (!this.isShowMode)
        await this.getDictionaries([
          dictionaryNames.PublicationForm,
          dictionaryNames.PublishingHouse,
          dictionaryNames.State,
          dictionaryNames.Year
        ])
      await this.getWorkTypes()
      await this.getMonography()
      this.dataLoaded = true
      this.isNoChanges = true
    },

    async getMonography() {
      try {
        if (this.$route.params?.id !== '0') {
          const data = this.$route.params?.id ? await monographyApi.get(this.$route.params?.id) : null
          await this.getFileList(data.commonWork?.id)
          if (data) this.monography = new Monography(data)
        }
      } catch (e) {
        console.log(e)
        this.showErrorMessage(e, 'Не удалось получить данные по работе')
      }
    },

    async saveMonography(isThrowError = false) {
      try {
        this.saveBtnLoading = true

        if (!this.monography.id) this.monography.commonWork.workType = this.workTypeByName(workTypeName.Monography)

        const body = this.monography.getForAPI()

        const data = this.monography.id ? await monographyApi.update(body) : await monographyApi.create(body)

        await this.removeFileFromAPI()

        const linkFileListForSave = this.linkFileList.filter(file => !file.id)
        const agreementFileListForSave = this.agreementFileList.filter(file => !file.id)

        if (linkFileListForSave.length) await this.saveFile(data?.commonWork?.id, linkFileListForSave, fileTypeId.link)
        if (agreementFileListForSave.length)
          await this.saveFile(data?.commonWork?.id, agreementFileListForSave, fileTypeId.agreement)

        if (!this.monography.id) await this.$router.replace('/works/monography/edit/' + data.id)

        await this.getFileList(data.commonWork?.id)
        this.monography = new Monography(data)
        this.$toast.info('Работа успешно сохранена')
        queueMicrotask(() => (this.isNoChanges = true))
      } catch (e) {
        console.log(e)
        if (e.message === 'Ошибка удаления файлов') this.getFileList(this.monography.commonWork?.id)
        else this.showErrorMessage(e, 'Ошибка сохранения работы')
        if (isThrowError) throw new Error('Ошибка сохранения')
      } finally {
        this.saveBtnLoading = false
      }
    },

    initUploadLinkFiles(files) {
      this.uploadLinkFiles(files)
      this.isNoChanges = false
    },

    initRemoveLinkFile(index) {
      this.removeLinkFile(index)
      this.isNoChanges = false
    },

    initUploadAgreementFile(file) {
      this.uploadAgreementFile(file)
      this.isNoChanges = false
    },

    initRemoveAgreementFile(index) {
      this.removeAgreementFile(index)
      this.isNoChanges = false
    },

    updateAuthorsInvalid(val) {
      this.authorsInvalid = val
      this.isNoChanges = false
    },

    updateMonographyInvalid(val) {
      this.monographyInvalid = val
      this.isNoChanges = false
    },

    updatePagesInTheEditionCardInvalid(val) {
      this.pagesInTheEditionCardInvalid = val
      this.isNoChanges = false
    },

    updatePublisherInformationCardInvalid(val) {
      this.publisherInformationCardInvalid = val
      this.isNoChanges = false
    }
  },

  watch: {
    dataLoaded() {
      this.setWorkListHeight()
    }
  },

  provide: function () {
    return {
      saveWork: this.saveMonography
    }
  }
}
</script>

<style scoped></style>
